// =============================================================================
// Integração do portal com o navegador pra virar "app do aluno" (PWA):
//  - <link rel="manifest"> do aluno (manifesto por slug, servido pela Pages
//    Function) — sem ele o navegador não oferece instalar;
//  - service worker mínimo (public/sw.js), fora de iframe e só em produção;
//  - captura do `beforeinstallprompt` o quanto antes (ele dispara uma vez, às
//    vezes antes do React montar) pra o convite usar o instalador nativo;
//  - memória de "já instalado" (evento `appinstalled` ou app aberto instalado).
// As regras de QUANDO convidar ficam em install-prompt.ts (puras/testadas).
// =============================================================================

import {
  detectPlatform,
  isInAppBrowser,
  isPreviewPath,
  readMemory,
  slugFromPath,
  writeMemory,
  type InstallPlatform,
} from "./install-prompt";
import { isValidSlug, portalManifestUrl } from "./pwa-manifest";

/** Evento do instalador nativo (Chrome/Edge/Samsung). Não está no lib.dom. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const OPEN_INSTALL_EVENT = "portal:open-install";
const PREVIEW_SESSION_KEY = "portal.previewSession";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Para useSyncExternalStore: muda quando o instalador nativo fica (in)disponível. */
export function subscribeInstallState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/** Descarta o instalador nativo depois de usado (ele só serve uma vez). */
export function takeDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  const prompt = deferredPrompt;
  deferredPrompt = null;
  notify();
  return prompt;
}

/** A memória do convite mudou (instalou/dispensou): quem depende dela reavalia. */
export function notifyInstallStateChanged(): void {
  notify();
}

export function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Já está rodando como app instalado. Só `standalone` (o display do manifesto):
 * `fullscreen` também casa com o navegador comum em tela cheia (F11/quiosque) e
 * marcaria "instalado" pra sempre por engano.
 */
export function isStandalone(): boolean {
  try {
    const nav = navigator as Navigator & { standalone?: boolean };
    return nav.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches === true;
  } catch {
    return false;
  }
}

export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Prévia do trainer ("Ver como aluno"): a URL de entrada traz `?preview=1` ou a
 * rota bridge; depois o aluno-fantasma navega e a query some — a marca fica na
 * sessionStorage da aba.
 */
export function isPreviewSession(pathname: string, search: string): boolean {
  const storage = safeSessionStorage();
  if (isPreviewPath(pathname, search)) {
    try {
      storage?.setItem(PREVIEW_SESSION_KEY, "1");
    } catch {
      // sem storage: vale só pra esta URL
    }
    return true;
  }
  return hasPreviewSessionFlag();
}

function hasPreviewSessionFlag(): boolean {
  try {
    return safeSessionStorage()?.getItem(PREVIEW_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export interface DeviceInfo {
  platform: InstallPlatform;
  inAppBrowser: boolean;
}

export function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent || "";
  return { platform: detectPlatform(ua, navigator.maxTouchPoints ?? 0), inAppBrowser: isInAppBrowser(ua) };
}

/**
 * Mostra o botão "Instalar o app" no Perfil? Mesmas travas do convite: nada no
 * app instalado, no iframe ou na prévia do trainer, nem depois de instalado; no
 * computador, só com o instalador nativo disponível.
 */
export function canOfferInstallFromMenu(slug: string | null): boolean {
  if (!slug || isStandalone() || isInIframe() || hasPreviewSessionFlag()) return false;
  if (readMemory(safeLocalStorage(), slug.toLowerCase()).installed) return false;
  const { platform } = getDeviceInfo();
  return platform !== "desktop" || deferredPrompt !== null;
}

/** Botão "Instalar o app" do Perfil → abre o convite. */
export function requestOpenInstallPrompt(): void {
  window.dispatchEvent(new Event(OPEN_INSTALL_EVENT));
}

export function onOpenInstallPromptRequest(listener: () => void): () => void {
  window.addEventListener(OPEN_INSTALL_EVENT, listener);
  return () => window.removeEventListener(OPEN_INSTALL_EVENT, listener);
}

/** Garante o <link rel="manifest"> do aluno da URL atual (e só dele). */
export function syncManifestLink(pathname: string): void {
  const slug = slugFromPath(pathname);
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!slug || !isValidSlug(slug) || isInIframe()) {
    existing?.remove();
    return;
  }
  const href = portalManifestUrl(slug);
  if (existing) {
    if (existing.getAttribute("href") !== href) existing.setAttribute("href", href);
    return;
  }
  const link = document.createElement("link");
  link.rel = "manifest";
  link.href = href;
  document.head.appendChild(link);
}

function rememberInstalled(): void {
  const slug = slugFromPath(window.location.pathname);
  if (slug) writeMemory(safeLocalStorage(), slug, { installed: true });
}

function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator) || isInIframe()) return;
  const register = () => {
    navigator.serviceWorker.register("/sw.js").catch((err: unknown) => {
      console.warn("[pwa] service worker não registrou", err);
    });
  };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}

/** Chamado uma vez no boot (main.tsx), antes do React montar. */
export function initPwa(): void {
  syncManifestLink(window.location.pathname);

  window.addEventListener("beforeinstallprompt", (event) => {
    // Segura o mini-banner do Chrome: quem convida é o nosso aviso, na hora certa.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    rememberInstalled();
    notify();
  });

  // Aberto como app: no Android o app instalado divide o armazenamento com o
  // Chrome, então o convite não volta quando o aluno abre o link no navegador.
  if (isStandalone()) rememberInstalled();

  registerServiceWorker();
}
